import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Users, Search, Plus, CheckCircle, AlertTriangle, Star,
  Phone, Mail, Globe, Wrench, X, Loader2, ShieldCheck,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { apiGet, apiPost, apiPatch } from '@/lib/api'
import { cn } from '@/lib/utils'

interface Vendor {
  id: string
  name: string
  tradingName: string | null
  email: string | null
  phone: string | null
  website: string | null
  specialisms: string[]
  status: string
  isGroupApproved: boolean
  rating: number | null
  registrationNo: string | null
  vatNo: string | null
  notes: string | null
  contacts: { id: string; name: string; email: string; phone: string; isPrimary: boolean }[]
  documents: { docType: string; expiresAt: string | null; isVerified: boolean }[]
  hasExpiringDocs: boolean
  hasExpiredDocs: boolean
  _count: { workOrders: number }
}

const STATUS_CONFIG: Record<string, { label: string; class: string }> = {
  ACTIVE: { label: 'Active', class: 'bg-green-100 text-green-800' },
  PENDING_APPROVAL: { label: 'Pending Approval', class: 'bg-amber-100 text-amber-800' },
  SUSPENDED: { label: 'Suspended', class: 'bg-red-100 text-red-800' },
  INACTIVE: { label: 'Inactive', class: 'bg-gray-100 text-gray-500' },
}

const SPECIALISMS = [
  'HVAC', 'Electrical', 'Plumbing', 'Lifts', 'Fire Safety',
  'Security', 'Cleaning', 'Landscaping', 'Painting', 'Carpentry',
  'Kitchen Equipment', 'Pool & Spa', 'IT & AV', 'Pest Control',
]

export function VendorsPage() {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterSpecialism, setFilterSpecialism] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)

  const [newVendor, setNewVendor] = useState({
    name: '', tradingName: '', email: '', phone: '',
    website: '', specialisms: [] as string[], registrationNo: '',
    vatNo: '', notes: '',
  })

  const isChiefPlus = ['GROUP_ADMIN', 'REGIONAL_DIRECTOR', 'PROPERTY_MANAGER', 'CHIEF_ENGINEER'].includes(user?.role ?? '')

  // ── Queries ─────────────────────────────────────────────────────────────
  const { data: vendors, isLoading } = useQuery({
    queryKey: ['vendors', search, filterStatus, filterSpecialism],
    queryFn: () =>
      apiGet<Vendor[]>('/vendors', {
        ...(search && { search }),
        ...(filterStatus && { status: filterStatus }),
        ...(filterSpecialism && { specialism: filterSpecialism }),
      }),
  })

  // ── Mutations ───────────────────────────────────────────────────────────
  const addMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/vendors', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      setShowAddModal(false)
      setNewVendor({ name: '', tradingName: '', email: '', phone: '', website: '', specialisms: [], registrationNo: '', vatNo: '', notes: '' })
    },
  })

  const approveMutation = useMutation({
    mutationFn: (vendorId: string) => apiPatch(`/vendors/${vendorId}/approve`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vendors'] }),
  })

  function handleAddSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!newVendor.name.trim()) return
    addMutation.mutate({
      name: newVendor.name.trim(),
      ...(newVendor.tradingName && { tradingName: newVendor.tradingName }),
      ...(newVendor.email && { email: newVendor.email }),
      ...(newVendor.phone && { phone: newVendor.phone }),
      ...(newVendor.website && { website: newVendor.website }),
      specialisms: newVendor.specialisms,
      ...(newVendor.registrationNo && { registrationNo: newVendor.registrationNo }),
      ...(newVendor.vatNo && { vatNo: newVendor.vatNo }),
      ...(newVendor.notes && { notes: newVendor.notes }),
    })
  }

  function toggleSpecialism(s: string) {
    setNewVendor(prev => ({
      ...prev,
      specialisms: prev.specialisms.includes(s)
        ? prev.specialisms.filter(x => x !== s)
        : [...prev.specialisms, s],
    }))
  }

  const list = vendors ?? []
  const activeCount = list.filter(v => v.status === 'ACTIVE').length
  const pendingCount = list.filter(v => v.status === 'PENDING_APPROVAL').length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-800">Contractors</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {list.length} contractors {pendingCount > 0 && `(${pendingCount} pending approval)`}
          </p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="btn-primary">
          <Plus className="w-4 h-4" />
          Add Contractor
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card py-4">
          <p className="text-xs text-gray-400">Total</p>
          <p className="text-2xl font-bold text-navy-800">{list.length}</p>
        </div>
        <div className="card py-4 border-green-200">
          <p className="text-xs text-gray-400">Active</p>
          <p className="text-2xl font-bold text-green-600">{activeCount}</p>
        </div>
        <div className="card py-4 border-amber-200">
          <p className="text-xs text-gray-400">Pending Approval</p>
          <p className={cn('text-2xl font-bold', pendingCount > 0 ? 'text-amber-600' : 'text-navy-800')}>
            {pendingCount}
          </p>
        </div>
        <div className="card py-4">
          <p className="text-xs text-gray-400">Doc Warnings</p>
          <p className={cn('text-2xl font-bold',
            list.some(v => v.hasExpiredDocs) ? 'text-red-600' : 'text-navy-800'
          )}>
            {list.filter(v => v.hasExpiredDocs || v.hasExpiringDocs).length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search contractors..."
              className="input pl-9"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input w-auto min-w-[150px]"
          >
            <option value="">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="PENDING_APPROVAL">Pending Approval</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="INACTIVE">Inactive</option>
          </select>
          <select
            value={filterSpecialism}
            onChange={(e) => setFilterSpecialism(e.target.value)}
            className="input w-auto min-w-[150px]"
          >
            <option value="">All Specialisms</option>
            {SPECIALISMS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Vendor cards */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-800" />
        </div>
      ) : list.length === 0 ? (
        <div className="card text-center py-16">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No contractors found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((vendor) => {
            const sc = STATUS_CONFIG[vendor.status] ?? STATUS_CONFIG.INACTIVE
            const primary = vendor.contacts[0]

            return (
              <div key={vendor.id} className="card hover:shadow-card-hover transition-shadow">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-navy-800 truncate">{vendor.name}</h3>
                    {vendor.tradingName && (
                      <p className="text-xs text-gray-400 truncate">t/a {vendor.tradingName}</p>
                    )}
                  </div>
                  <span className={cn('badge flex-shrink-0 ml-2', sc.class)}>{sc.label}</span>
                </div>

                {/* Warnings */}
                {vendor.hasExpiredDocs && (
                  <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 rounded-lg px-2 py-1.5 mb-3">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Expired documents
                  </div>
                )}
                {!vendor.hasExpiredDocs && vendor.hasExpiringDocs && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 rounded-lg px-2 py-1.5 mb-3">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Documents expiring soon
                  </div>
                )}

                {/* Contact info */}
                <div className="space-y-1.5 text-sm mb-3">
                  {(primary?.email || vendor.email) && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-3.5 h-3.5 text-gray-400" />
                      <span className="truncate text-xs">{primary?.email ?? vendor.email}</span>
                    </div>
                  )}
                  {(primary?.phone || vendor.phone) && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs">{primary?.phone ?? vendor.phone}</span>
                    </div>
                  )}
                  {vendor.website && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Globe className="w-3.5 h-3.5 text-gray-400" />
                      <span className="truncate text-xs">{vendor.website}</span>
                    </div>
                  )}
                </div>

                {/* Specialisms */}
                {vendor.specialisms.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {vendor.specialisms.map(s => (
                      <span key={s} className="badge bg-gray-100 text-gray-600">{s}</span>
                    ))}
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Wrench className="w-3.5 h-3.5" />
                      {vendor._count.workOrders} WOs
                    </span>
                    {vendor.rating && (
                      <span className="flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                        {vendor.rating}
                      </span>
                    )}
                    {vendor.isGroupApproved && (
                      <span className="flex items-center gap-1 text-green-600">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        Approved
                      </span>
                    )}
                  </div>

                  {isChiefPlus && vendor.status === 'PENDING_APPROVAL' && (
                    <button
                      onClick={() => approveMutation.mutate(vendor.id)}
                      disabled={approveMutation.isPending}
                      className="btn-secondary py-1 px-2.5 text-xs"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      Approve
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Add Vendor Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-navy-800 flex items-center gap-2">
                <Users className="w-5 h-5 text-navy-400" />
                Add Contractor
              </h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="label">Company Name *</label>
                  <input
                    value={newVendor.name}
                    onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                    className="input"
                    placeholder="e.g. Apex M&E Services Ltd"
                    required
                  />
                </div>
                <div>
                  <label className="label">Trading Name</label>
                  <input
                    value={newVendor.tradingName}
                    onChange={(e) => setNewVendor({ ...newVendor, tradingName: e.target.value })}
                    className="input"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input
                    type="email"
                    value={newVendor.email}
                    onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                    className="input"
                    placeholder="operations@company.com"
                  />
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input
                    value={newVendor.phone}
                    onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })}
                    className="input"
                    placeholder="+44 20 7000 1234"
                  />
                </div>
                <div>
                  <label className="label">Website</label>
                  <input
                    value={newVendor.website}
                    onChange={(e) => setNewVendor({ ...newVendor, website: e.target.value })}
                    className="input"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="label">Registration No.</label>
                  <input
                    value={newVendor.registrationNo}
                    onChange={(e) => setNewVendor({ ...newVendor, registrationNo: e.target.value })}
                    className="input"
                    placeholder="Companies House #"
                  />
                </div>
                <div>
                  <label className="label">VAT No.</label>
                  <input
                    value={newVendor.vatNo}
                    onChange={(e) => setNewVendor({ ...newVendor, vatNo: e.target.value })}
                    className="input"
                    placeholder="GB 123 4567 89"
                  />
                </div>
              </div>

              <div>
                <label className="label">Specialisms</label>
                <div className="flex flex-wrap gap-1.5">
                  {SPECIALISMS.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleSpecialism(s)}
                      className={cn(
                        'badge cursor-pointer transition-colors',
                        newVendor.specialisms.includes(s)
                          ? 'bg-navy-800 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label">Notes</label>
                <textarea
                  value={newVendor.notes}
                  onChange={(e) => setNewVendor({ ...newVendor, notes: e.target.value })}
                  rows={2}
                  className="input resize-none"
                  placeholder="Any additional notes..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newVendor.name.trim() || addMutation.isPending}
                  className="btn-primary"
                >
                  {addMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Adding...</>
                  ) : (
                    <><Plus className="w-4 h-4" /> Add Contractor</>
                  )}
                </button>
              </div>

              {addMutation.isError && (
                <p className="text-sm text-red-600">Failed to add contractor. Please try again.</p>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
